import * as Sentry from '@sentry/react';

export interface AdminUser {
  id: string;
  email: string;
  name?: string;
  isAuthenticated: boolean;
  twoFactorEnabled?: boolean;
  lastLogin?: string;
  createdAt?: string;
}

export interface SentryUserContext {
  id: string;
  email?: string;
  name?: string;
  role?: string;
}

export const sentryContextService = {
  /**
   * Set user context in Sentry for better error tracking
   */
  setUserContext(user: AdminUser | SentryUserContext): void {
    Sentry.setUser({
      id: user.id,
      // Don't include email in production for privacy
      ...(import.meta.env.DEV && { email: user.email }),
      username: user.name,
      // Custom attributes for admin users
      role: 'admin',
      ...(('isAuthenticated' in user) && { authenticated: user.isAuthenticated }),
      ...(('twoFactorEnabled' in user) && { twoFactorEnabled: user.twoFactorEnabled }),
      ...(('lastLogin' in user) && { lastLogin: user.lastLogin }),
    });

    // Set custom tags
    Sentry.setTag('user_role', 'admin');
    Sentry.setTag('application', 'administration-portal');
    if ('twoFactorEnabled' in user) {
      Sentry.setTag('2fa_enabled', user.twoFactorEnabled ? 'true' : 'false');
    }
  },

  /**
   * Clear user context (on logout)
   */
  clearUserContext(): void {
    Sentry.setUser(null);
    Sentry.setTag('user_role', null);
    Sentry.setTag('2fa_enabled', null);
  },

  /**
   * Set custom context for a specific feature or page
   */
  setFeatureContext(feature: string, data?: Record<string, any>): void {
    Sentry.setContext('feature', {
      name: feature,
      ...data,
    });
  },

  /**
   * Add breadcrumb for user actions
   */
  addBreadcrumb(message: string, category: string = 'admin', level: 'info' | 'error' | 'warning' | 'debug' = 'info', data?: Record<string, any>): void {
    Sentry.addBreadcrumb({
      message,
      category,
      level,
      data,
      timestamp: Date.now() / 1000,
    });
  },

  /**
   * Set session context for better tracking
   */
  setSessionContext(sessionData: {
    sessionId?: string;
    route?: string;
    referrer?: string;
    userAgent?: string;
  }): void {
    Sentry.setContext('session', {
      id: sessionData.sessionId || crypto.randomUUID(),
      route: sessionData.route || window.location.pathname,
      referrer: sessionData.referrer || document.referrer,
      userAgent: sessionData.userAgent || navigator.userAgent,
      timestamp: new Date().toISOString(),
    });
  },

  /**
   * Track performance for key user interactions
   */
  startTransaction(name: string, operation: string = 'navigation') {
    return Sentry.startSpan({
      name,
      op: operation,
      attributes: {
        application: 'administration-portal',
        user_role: 'admin',
      },
    }, () => {
      // Return a simple object with finish method for compatibility
      return {
        finish: () => {},
        startChild: (_spanData: any) => ({
          setData: () => {},
          finish: () => {},
          setStatus: () => {},
        }),
      };
    });
  },

  /**
   * Capture custom exception with context
   */
  captureException(error: Error, context?: {
    tags?: Record<string, string>;
    extra?: Record<string, any>;
    level?: 'error' | 'warning' | 'info' | 'debug';
  }): void {
    Sentry.withScope((scope) => {
      if (context?.tags) {
        Object.entries(context.tags).forEach(([key, value]) => {
          scope.setTag(key, value);
        });
      }

      if (context?.extra) {
        Object.entries(context.extra).forEach(([key, value]) => {
          scope.setExtra(key, value);
        });
      }

      if (context?.level) {
        scope.setLevel(context.level);
      }

      Sentry.captureException(error);
    });
  },

  /**
   * Capture custom message with context
   */
  captureMessage(message: string, level: 'error' | 'warning' | 'info' | 'debug' = 'info', context?: {
    tags?: Record<string, string>;
    extra?: Record<string, any>;
  }): void {
    Sentry.withScope((scope) => {
      if (context?.tags) {
        Object.entries(context.tags).forEach(([key, value]) => {
          scope.setTag(key, value);
        });
      }

      if (context?.extra) {
        Object.entries(context.extra).forEach(([key, value]) => {
          scope.setExtra(key, value);
        });
      }

      scope.setLevel(level);
      Sentry.captureMessage(message);
    });
  },
};